/**
 * Раскладка левого списка. Всё, что есть в глобальном base.css (.aside, .aside-h, .rows,
 * .row, .fchip, .pill), здесь НЕ переопределяется: соответствие макету проверяется сверкой
 * классов, а не чтением двух файлов сразу. Тут только то, чего в макете не было, потому что
 * макет статичен: скролл, курсор и hover у настоящих кнопок и ссылок.
 */

/* Полоса фильтров. В макете чипы стоят над таблицей; в колонке 380px они переносятся. */
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-2);
}

/*
 * Чип фильтра — <button>, а не <span> из макета: он нажимается, значит обязан быть кнопкой
 * (клавиатура, роль, aria-pressed). Кнопка не наследует шрифт документа — возвращаем его.
 */
.chip {
  font-family: var(--sans);
  line-height: 1.2;
  cursor: pointer;
}
.chip:hover {
  border-color: var(--violet-bg-2);
}

/* Скролл держит обёртка, а не .rows: у .rows в base.css overflow:hidden, и переопределять
   глобальный класс из модуля — гонка порядка стилей, а не решение. */
.list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.rowLink {
  cursor: pointer;
  text-decoration: none;
}
.rowLink:hover {
  background: var(--violet-bg);
}

/* Состояния «пусто», «ошибка», «загрузка» — один и тот же блок с воздухом. */
.state {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 16px;
}

/* Кнопка «показать все» внутри пустого фильтра: ссылка по виду, кнопка по сути. */
.linkBtn {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-family: var(--sans);
  font-size: 13px;
  color: var(--accent-ink);
  cursor: pointer;
}
.linkBtn:hover {
  color: var(--accent-ink-strong);
}

/* ─── свёрнутая панель ──────────────────────────────────────────────────────
 * Правка заказчика 26.08: на экране черновика 380px подписей нужны при ВЫБОРЕ поста и
 * мешают при ЧТЕНИИ. Свёрнутая полоса оставляет достижимость в один клик и отдаёт ширину
 * тексту. Ширина 60px — это кружок 34px плюс поля: меньше не даёт попасть пальцем, больше
 * начинает притворяться списком, которым уже не является.
 */
.railAside {
  /*
   * Свёрнутая полоса НЕ переиспользует глобальный `.aside`, и это не вкусовщина: у `.aside`
   * стоит `width: 380px; max-width: 40vw`, специфичность у обоих селекторов одинаковая
   * (один класс), и победа решается порядком подключения файлов — то есть случайностью
   * сборки. Первый заход именно так и развалился: полоса растянулась на 40vw. Собственный
   * класс убирает спор целиком, а четыре общих свойства (фон, граница, колонка) дешевле
   * скопировать, чем поддерживать войну специфичностей.
   */
  width: 60px;
  min-width: 60px;
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--white);
  border-right: 1px solid var(--border-2);
}

.railHead {
  display: flex;
  justify-content: center;
  padding: 12px 0 10px;
  width: 100%;
  border-bottom: 1px solid var(--border-2);
}

.rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
}

/*
 * Кружок несёт первую букву заголовка: соседние посты обязаны различаться до наведения,
 * иначе полоса превращается в ряд одинаковых точек и выбирать в ней нечего.
 */
.dot {
  position: relative;
  font-variant-numeric: tabular-nums;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  border: 1px solid var(--border-2);
  background: var(--white);
  color: var(--ink-2);
  transition:
    transform 0.12s ease,
    border-color 0.12s ease;
}
.dot:hover {
  border-color: var(--violet-bg-2);
  transform: scale(1.06);
}

/* Ждёт решения — единственное состояние, зовущее человека действовать прямо сейчас, и
   единственное, которому отдан фирменный фиолетовый. */
.dotAwaits {
  background: var(--violet);
  border-color: var(--violet);
  color: var(--on-accent);
}

/* Открытый пост: кольцо снаружи, чтобы не спорить с заливкой «ждёт решения». */
.dotOn {
  box-shadow:
    0 0 0 2px var(--white),
    0 0 0 4px var(--violet);
}

/*
 * Точка статуса в углу кружка. Цвет берётся у той же пилюли, что и в развёрнутом списке
 * (классы st-*), поэтому «красный» здесь и «красный» там — один и тот же красный.
 */
.dotState {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid var(--white);
  padding: 0;
}

/* Кнопка сворачивания. В развёрнутом виде прижата вправо шапки, в свёрнутом — по центру. */
.toggle {
  margin-left: auto;
  flex: none;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  border: 1px solid var(--border-2);
  background: var(--white);
  color: var(--ink-2);
  cursor: pointer;
}
.toggle:hover {
  border-color: var(--violet-bg-2);
  color: var(--accent-ink);
}
.railHead .toggle {
  margin-left: 0;
}

/* Обёртка тела нужна только для aria-controls у кнопки: сама раскладка — у .list. */
.bodyWrap {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/*
 * Подпись для чтения с экрана. Кружок несёт букву и цвет — глазами этого хватает, голосом
 * нет: без неё скринридер прочитал бы ссылку как одну букву. Класса в base.css нет, а
 * заводить глобальный ради одного места — расширять общий словарь под частный случай.
 */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/*
 * ─── телефон (640) ─────────────────────────────────────────────────────────
 *
 * В одну колонку (`.body` в Shell.module.css) свёрнутая полоса перестаёт быть полосой:
 * вертикальный столбец кружков шириной 60px над постом отбирал бы экран по высоте и ничего
 * не давал взамен. Поэтому она ложится ЛЕНТОЙ — тот же ряд кружков, только слева направо, с
 * горизонтальной прокруткой вместо вертикальной. Достижимость поста в один клик, ради
 * которой свёрнутый вид и существует, сохраняется дословно.
 *
 * Развёрнутый список во всю ширину (`.aside` в base.css) ограничен по высоте: без потолка он
 * занимал бы весь первый экран и пост уезжал бы под него. 45vh — примерно четыре строки
 * списка при высоте строки 56–64px (§4), то есть выбор виден, а пост начинается на том же
 * экране.
 */
@media (max-width: 640px) {
  .railAside {
    width: 100%;
    min-width: 0;
    flex-direction: row;
    align-items: center;
    border-right: none;
    border-bottom: 1px solid var(--border-2);
  }
  .railHead {
    width: auto;
    flex: none;
    padding: 8px 10px;
    border-bottom: none;
    border-right: 1px solid var(--border-2);
  }
  .rail {
    flex-direction: row;
    padding: 8px 10px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
  }
  .rail::-webkit-scrollbar {
    display: none;
  }
  .list {
    max-height: 45vh;
  }
}
